// List kit [02 §6.3]: cursor pagination (opaque base64url of sort-key values + id), limit 50/200,
// `include=total` capped at 10 000, declared sort keys only.
import { z } from 'zod';
import { AppError } from './errors.ts';

export const LIST_LIMIT_DEFAULT = 50;
export const LIST_LIMIT_MAX = 200;
export const LIST_TOTAL_CAP = 10_000;
export const EXPORT_STREAM_MAX_ROWS = 5_000;

export const limitSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(LIST_LIMIT_MAX)
  .default(LIST_LIMIT_DEFAULT);

export type CursorValue = string | number | boolean | null;

export interface Cursor {
  /** Values of the declared sort keys for the boundary row, in sort order. */
  readonly keys: readonly CursorValue[];
  /** Stable tiebreak (entity id). */
  readonly id: string;
  readonly direction: 'next' | 'prev';
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function base64UrlEncode(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const n = (a << 16) | (b << 8) | c;
    out += (B64[(n >> 18) & 63] ?? '') + (B64[(n >> 12) & 63] ?? '');
    if (i + 1 < bytes.length) out += B64[(n >> 6) & 63] ?? '';
    if (i + 2 < bytes.length) out += B64[n & 63] ?? '';
  }
  return out;
}

export function base64UrlDecode(text: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/.test(text) || text.length % 4 === 1)
    throw new RangeError('bad base64url');
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 4) {
    const chunk = text.slice(i, i + 4);
    const v = [...chunk].map((ch) => B64.indexOf(ch));
    const n = ((v[0] ?? 0) << 18) | ((v[1] ?? 0) << 12) | ((v[2] ?? 0) << 6) | (v[3] ?? 0);
    out.push((n >> 16) & 255);
    if (chunk.length > 2) out.push((n >> 8) & 255);
    if (chunk.length > 3) out.push(n & 255);
  }
  return Uint8Array.from(out);
}

const cursorPayload = z.tuple([
  z.enum(['n', 'p']),
  z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
  z.string().min(1),
]);

export function encodeCursor(cursor: Cursor): string {
  const payload = [cursor.direction === 'next' ? 'n' : 'p', cursor.keys, cursor.id];
  return base64UrlEncode(new TextEncoder().encode(JSON.stringify(payload)));
}

/** Decodes a client cursor; anything malformed is a 400 (never a 500). */
export function decodeCursor(text: string): Cursor {
  try {
    const raw: unknown = JSON.parse(
      new TextDecoder('utf-8', { fatal: true }).decode(base64UrlDecode(text)),
    );
    const [dir, keys, id] = cursorPayload.parse(raw);
    return { direction: dir === 'n' ? 'next' : 'prev', keys, id };
  } catch (cause) {
    throw new AppError('VALIDATION_FAILED', {
      fields: [{ path: 'cursor', code: 'invalid_cursor', messageKey: 'errors.list.invalidCursor' }],
      cause,
    });
  }
}

export interface SortTerm<K extends string = string> {
  readonly key: K;
  readonly direction: 'asc' | 'desc';
}

/** Parses `?sort=-orderDate,number` against declared keys; unknown keys are a 400. */
export function parseSort<K extends string>(
  raw: string | undefined,
  allowed: readonly K[],
  fallback: readonly SortTerm<K>[] = [],
): SortTerm<K>[] {
  if (raw === undefined || raw.trim() === '') return [...fallback];
  const terms: SortTerm<K>[] = [];
  for (const part of raw.split(',')) {
    const t = part.trim();
    const desc = t.startsWith('-');
    const key = desc ? t.slice(1) : t;
    if (!(allowed as readonly string[]).includes(key)) {
      throw new AppError('VALIDATION_FAILED', {
        fields: [
          {
            path: 'sort',
            code: 'unknown_sort_key',
            messageKey: 'errors.list.unknownSort',
            params: { key },
          },
        ],
      });
    }
    if (terms.some((x) => x.key === key)) continue;
    terms.push({ key: key as K, direction: desc ? 'desc' : 'asc' });
  }
  return terms;
}

/** Applies the 10 000 cap to a counted total [02 §6.3]. */
export function capTotal(count: number): { total: number; totalCapped: boolean } {
  return count > LIST_TOTAL_CAP
    ? { total: LIST_TOTAL_CAP, totalCapped: true }
    : { total: count, totalCapped: false };
}

/** Base list query (filters are declared per list by extending this schema). */
export const listQuerySchema = z.object({
  limit: limitSchema,
  cursor: z.string().optional(),
  sort: z.string().optional(),
  q: z.string().trim().max(200).optional(),
  include: z
    .string()
    .optional()
    .transform((v) =>
      v
        ? v
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    ),
});
export type ListQuery = z.infer<typeof listQuerySchema>;
